import * as React from "react";
import { useHistory } from "react-router-dom";
import Slider, { Settings } from "react-slick";
import clsx from "clsx";
import { useSwipeable } from "react-swipeable";

import { parseQueryParams } from "../../../libs/Util";
import { ILesson } from "../../../state";
import { Context } from "../../../teacherStore";
import {
  ARROW_DOWN,
  ARROW_RIGHT,
  ARROW_UP,
  ARROW_LEFT,
} from "../../../constants/key";
import useDebounceCallback from "../../../hooks/useDebounceCallback";
import VideoJS from "../../../atoms/VideoJS";
import Button from "../../../atoms/ActiveLearning/Button";
import LessonCompleteConfirmModal from "../../../molecules/LessonCompleteConfirmModal";
import ModalAnswers from "../ModalAnswers";
import ModalThreeRules from "../../../organisms/teacher/ModalThreeRules";
import Timer from "../../../organisms/teacher/Timer";
import ImagePageView from "../../../organisms/teacher/ImagePageView";
import ModalEditGroupClass from "../ModalEditGroupClass";
import EvaluationLessonModal from "../../../organisms/teacher/EvaluationLessonModal";
import BeforeModal from "../../../organisms/OnePagePortfolioAssessment/BeforeModal";
import AfterModal from "../../../organisms/OnePagePortfolioAssessment/AfterModal";
import { ReactComponent as ArrowIcon } from "../../../assets/svg/arrow_right_single.svg";
import { ReactComponent as IconClose } from "../../../assets/svg/close.svg";
import { ReactComponent as NaviIcon } from "../../../assets/svg/navi.svg";
import { ReactComponent as ArrowLeftIcon } from "../../../assets/svg/arrow_left_4.svg";
import { ReactComponent as PencilIcon } from "../../../assets/svg/pencil_2.svg";
import { TEACHER_SESSION_KEY } from "../../../Definition";
import ActionBubbleIcon from "../../../atoms/ActionBubbleIcon";
import ActionBubbleModal from "../../../atoms/ActionBubbleModal";
import ProjectionScreenModal from "../ProjectionScreenModal";
import { useDraggable } from "../../../hooks/useDraggable";
import DropdownMenu from "../../../atoms/DropdownMenu";

import styles from "./MaterialForImage.module.scss";

export interface MaterialForImageProps {
  teachingMaterialId: number;
  lesson: ILesson;
  classList: number[];
  prevLessonId: number | null;
  nextLessonId: number | null;
  maxRoundNo: number;
  roundNo: number;
  postCompletedLesson: () => void;
  postCompletedLessonRetrospectives: (
    score: number,
    lessonDeliveryScore: number,
    groupWorkScore: number | null,
  ) => void;
  fetchOnePagePortfolioAssessment: (id: number) => void;
  fetchOnePagePortfolioAssessmentAnswersVisibility: (
    id: number,
    classList: number[],
  ) => void;
  hasVideoChapter?: boolean;
  onSwitchToVideo: () => void;
  isFromVideoLesson?: boolean;
}

interface SubPageState {
  page: number;
  subPage: number;
}

const settings: Settings = {
  accessibility: false,
  arrows: false,
  dots: false,
  infinite: false,
  speed: 300,
  slidesToShow: 1,
  slidesToScroll: 1,
  swipe: false,
  vertical: true,
  waitForAnimate: false,
};

const existsPage = (
  images: {
    page_title?: string;
    estimated_time?: number;
    image_url: string;
  }[],
  page: number,
) => !!images[page - 1].image_url;

const existsSubPage = (subImages: string[][], page: number, subPage: number) =>
  subImages[page - 1] && subImages[page - 1][subPage - 1];

const MaterialForImage = (props: MaterialForImageProps) => {
  const { state } = React.useContext(Context);
  const history = useHistory();
  const slider = React.useRef<Slider>(null);
  const [actionModal, setActionModal] = React.useState<boolean>(false);
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const [wrapperSize, setWrapperSize] = React.useState({ width: 0, height: 0 });
  const [debounceClickOutside] = useDebounceCallback(
    (listButton: HTMLCollectionOf<Element>) => {
      for (let i = 0; i < listButton.length; i++) {
        setTimeout(() => {
          if (listButton.item(i)?.classList.contains(styles.buttonFadeIn)) {
            listButton.item(i)?.classList.remove(styles.buttonFadeIn);
          }
        }, 1700);
      }
    },
  );
  const [debounceClick] = useDebounceCallback((action: () => void) => {
    action();
  });
  const [debounceSwipe] = useDebounceCallback((action: () => void) => {
    action();
  }, 400);

  const [isShowAnswersModal, setIsShowAnswersModal] = React.useState(false);
  const [isShowThreeRules, setIsShowThreeRules] = React.useState(false);
  const [showVideo, setShowVideo] = React.useState(false);
  const [showModalEditGroupClass, setShowModalEditGroupClass] = React.useState(
    false,
  );
  const [showTimer, setShowTimer] = React.useState(false);
  const [videoOptions, setVideoOptions] = React.useState({
    url: "",
    playing: false,
  });
  const [showLessonImageMenu, setShowLessonImageMenu] = React.useState(
    !!props.isFromVideoLesson,
  );
  const [
    isShowLessonCompleteConfirmModal,
    setIsShowLessonCompleteConfirmModal,
  ] = React.useState(false);
  const [
    isShowEvaluationLessonModal,
    setIsShowEvaluationLessonModal,
  ] = React.useState(false);
  const [
    onePagePortfolioAssessmentId,
    setOnePagePortfolioAssessmentId,
  ] = React.useState<number | undefined>(undefined);

  const prmPage = Number(parseQueryParams()["page"]) || 1;
  const prmSub = Number(parseQueryParams()["sub_page"]) || 0;
  const [
    prevSubPageState,
    setPrevSubPageState,
  ] = React.useState<SubPageState | null>(null);
  const isShowSub = !!props.lesson.sub_images && prmSub > 0;
  const subImages = React.useMemo(() => props.lesson.sub_images || [], [
    props.lesson.sub_images,
  ]);
  const images = React.useMemo(() => {
    return props.lesson.images
      ? props.lesson.images.map(image => image.image_url)
      : [];
  }, [props.lesson.images]);
  const totalEstimatedTime = React.useMemo(() => {
    return props.lesson.images.reduce(
      (prev, current) => prev + (current.estimated_time || 0),
      0,
    );
  }, [props.lesson.images]);

  const currentPage = existsPage(props.lesson.images, prmPage) ? prmPage : 1;
  const currentSubPage =
    isShowSub && existsSubPage(subImages, prmPage, prmSub) ? prmSub : 1;
  const currentFlatSubPage = React.useMemo(
    () =>
      isShowSub
        ? subImages.slice(0, currentPage - 1).flat().length + currentSubPage
        : 1,
    [isShowSub, subImages, currentPage, currentSubPage],
  );
  const currentVideo = React.useMemo(
    () =>
      props.lesson.videos?.find(
        video =>
          video.page === currentPage && (!isShowSub || video.sub_page !== null),
      ),
    [isShowSub, currentPage, props.lesson.videos],
  );

  const flattenSubImages = React.useMemo(() => subImages.flat(), [subImages]);

  const currentIssue = React.useMemo(
    () => props.lesson.issues?.find(issue => issue.page === currentPage),
    [props.lesson.issues, currentPage],
  );
  const issueID = currentIssue?.id || 0;

  const currentPageOPPAId = React.useMemo(() => {
    const images = props.lesson.images;
    const index = currentPage - 1;
    if (index < 0 || images.length <= index) return;
    return images[index].one_page_portfolio_assessment_id;
  }, [props.lesson.images, currentPage]);

  React.useEffect(() => {
    if (isShowSub) {
      return;
    }

    if (!!currentPageOPPAId) {
      props.fetchOnePagePortfolioAssessment(currentPageOPPAId);
      props.fetchOnePagePortfolioAssessmentAnswersVisibility(
        currentPageOPPAId,
        props.classList,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPageOPPAId, isShowSub]);

  React.useEffect(() => {
    const nextIdx = isShowSub ? currentFlatSubPage - 1 : currentPage - 1;
    slider.current?.slickGoTo(nextIdx, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, currentSubPage]);

  React.useEffect(() => {
    if (props.lesson) {
      if (currentVideo) {
        setVideoOptions({
          url: currentVideo.video_url,
          playing: false,
        });
      } else {
        setVideoOptions({
          url: "",
          playing: false,
        });
      }
    }
  }, [props.lesson, currentVideo]);

  React.useEffect(() => {
    const changePageByKeyBoard = (e: any) => {
      if (onePagePortfolioAssessmentId) return;

      if (/input|textarea/i.test(e.target.tagName)) return;

      if (e.key === ARROW_DOWN || e.key === ARROW_RIGHT) {
        debounceClick(() => nextFunc());
      }

      if (e.key === ARROW_UP || e.key === ARROW_LEFT) {
        debounceClick(prevFunc);
      }
    };

    if (isShowAnswersModal || isShowThreeRules) {
      document.removeEventListener("keydown", changePageByKeyBoard);
    } else {
      document.addEventListener("keydown", changePageByKeyBoard);
    }

    return () => {
      document.removeEventListener("keydown", changePageByKeyBoard);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isShowSub,
    currentPage,
    currentSubPage,
    isShowAnswersModal,
    isShowThreeRules,
    onePagePortfolioAssessmentId,
  ]);

  const disablePrev = currentPage === 1 && (!isShowSub || currentSubPage === 1);

  const disableNext = isShowSub
    ? currentFlatSubPage >= flattenSubImages.length
    : currentPage >= props.lesson.images.length;

  const roundOption = Array.from({ length: props.maxRoundNo }, (_, index) => ({
    label: `シート(${index + 1})`,
    value: index + 1,
  }));

  const handleChangeRoundNo = (roundNo: number) => {
    const roundNoParam = roundNo > 1 ? `&round=${roundNo}` : "";
    history.push({
      search: `?teachingMaterial=${props.teachingMaterialId}&lesson=${props.lesson.id}&page=1&classList=${props.classList}${roundNoParam}`,
    });
  };

  const setNewPage = (newPage: number, newSubPage?: number) => {
    const query = newSubPage
      ? `?teachingMaterial=${props.teachingMaterialId}&lesson=${props.lesson.id}&page=${newPage}&sub_page=${newSubPage}&classList=${props.classList}`
      : `?teachingMaterial=${props.teachingMaterialId}&lesson=${props.lesson.id}&page=${newPage}&classList=${props.classList}`;
    history.push({
      search: query,
    });
  };

  const checkOPPA = () => {
    if (isShowSub) return false;
    if (onePagePortfolioAssessmentId) return true;

    if (currentPageOPPAId) {
      setOnePagePortfolioAssessmentId(currentPageOPPAId);
      return true;
    }
    return false;
  };

  const prevFunc = () => {
    const isZoom = Math.abs(document.body.offsetWidth - window.innerWidth) > 3;
    if (isZoom || disablePrev) {
      return;
    }
    if (isShowSub) {
      const prevPage = currentSubPage > 1 ? currentPage : currentPage - 1;
      const prevSubPage =
        currentSubPage > 1
          ? currentSubPage - 1
          : subImages[prevPage - 1].length;
      setNewPage(prevPage, prevSubPage);
    } else {
      setNewPage(currentPage - 1);
    }
  };

  const nextFunc = (force = false) => {
    const isZoom = Math.abs(document.body.offsetWidth - window.innerWidth) > 3;
    if (isZoom || disableNext) {
      return;
    }
    if (!force && checkOPPA()) return;
    if (isShowSub) {
      const exists = currentSubPage < subImages[currentPage - 1].length;
      const nextPage = exists ? currentPage : currentPage + 1;
      const nextSubPage = exists ? currentSubPage + 1 : 1;
      setNewPage(nextPage, nextSubPage);
    } else {
      setNewPage(currentPage + 1);
    }
  };

  const onChangeLesson = (lessonId: number, roundNo?: number) => {
    const roundNoParam = roundNo && roundNo > 1 ? `&round=${roundNo}` : "";
    history.push({
      search: `?teachingMaterial=${props.teachingMaterialId}&lesson=${lessonId}&page=1&classList=${props.classList}${roundNoParam}`,
    });
  };

  const prevLesson = () => {
    if (props.roundNo > 1) {
      onChangeLesson(props.lesson.id, props.roundNo - 1);
    } else {
      props.prevLessonId && onChangeLesson(props.prevLessonId);
    }
  };

  const nextLesson = () => {
    const roundNo = props.roundNo || 1;
    if (props.maxRoundNo > roundNo) {
      onChangeLesson(props.lesson.id, roundNo + 1);
    } else {
      props.nextLessonId && onChangeLesson(props.nextLessonId);
    }
  };

  const handlers = useSwipeable({
    onSwipedUp: () => debounceSwipe(() => nextFunc()),
    onSwipedDown: () => debounceSwipe(prevFunc),
    delta: 60,
    swipeDuration: 500,
  });

  const refPassthrough = (el: HTMLDivElement) => {
    // call useSwipeable ref prop with el
    handlers.ref(el);

    // set myRef el so you can access it yourself
    //@ts-ignore
    wrapperRef.current = el;
  };

  const handleWrapperRef = () => {
    if (wrapperRef.current) {
      setWrapperSize({
        width: wrapperRef.current.offsetWidth,
        height: wrapperRef.current.offsetHeight,
      });
    }
  };

  React.useEffect(() => {
    handleWrapperRef();
    window.addEventListener("resize", handleWrapperRef);

    return () => {
      window.removeEventListener("resize", handleWrapperRef);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wrapperRef.current]);

  const {
    position: timerPos,
    onMouseDown: onTimerDragStart,
    reset: resetTimerPos,
  } = useDraggable(0, 0);

  React.useEffect(() => {
    if (!showTimer) resetTimerPos();
  }, [showTimer, resetTimerPos]);

  const handleClickOutside = () => {
    if (showLessonImageMenu) {
      setShowLessonImageMenu(false);
      // 外部クリックで教材メニューを閉じる際は、上下部の矢印を表示しない
      return;
    }

    const listButton = document.getElementsByClassName(styles.paginate);
    for (let i = 0; i < listButton.length; i++) {
      if (!listButton.item(i)?.classList.contains(styles.buttonFadeIn)) {
        listButton.item(i)?.classList.add(styles.buttonFadeIn);
      }
    }
    debounceClickOutside(listButton);
  };

  const handleCloseVideo = () => {
    setVideoOptions(prevState => ({
      ...prevState,
      playing: false,
    }));
    setShowVideo(false);
  };

  const handleOpenVideo = () => {
    setShowVideo(true);
    if (currentVideo) {
      setVideoOptions({
        url: currentVideo.video_url,
        playing: false,
      });
    } else {
      setVideoOptions({
        url: "",
        playing: false,
      });
    }
  };

  const getPercentage = () => {
    if (!props.lesson.images) return 0;
    const target_estimated_times = props.lesson.images.filter(
      image => currentPage >= image.page_no && image.estimated_time,
    );
    const currentEstimatedTime = target_estimated_times.reduce(
      (prev, current) => prev + (current.estimated_time || 0),
      0,
    );
    return Math.floor((currentEstimatedTime / totalEstimatedTime) * 100);
  };

  const getCircleStyle = () => {
    const deg = (360 / 100) * getPercentage();
    if (deg === 360 || deg === 0) {
      return {
        background: `conic-gradient(
          #FFFFFF 0deg ${deg}deg,
          #ADB5BD ${deg}deg 360deg
        )`,
      };
    }
    return {
      background: `conic-gradient(
        #FFFFFF 0deg ${deg}deg,
        #ADB5BD ${deg}deg 360deg
      )`,
    };
  };

  const completion_statuses = state.completion_statuses.completion_statuses;
  const hasLessonCompletion =
    completion_statuses.length === 0 ||
    completion_statuses.some(status => status.is_completed);

  const isShowCompleteLessonButton =
    disableNext && !!props.nextLessonId && !hasLessonCompletion;

  const isShowActionModal =
    currentPage === props.lesson.images.length &&
    props.lesson?.is_retrospective;

  const OPPAType = React.useMemo(() => {
    if (!state.reference_issues) return null;
    return state.reference_issues.length === 0 ? "before" : "after";
  }, [state.reference_issues]);

  const extra = sessionStorage.getItem(TEACHER_SESSION_KEY)
    ? `&session_id=${sessionStorage.getItem(TEACHER_SESSION_KEY)}`
    : "";

  return (
    <div className="d-flex h-100">
      <div className={styles.wrapper} {...handlers} ref={refPassthrough}>
        <button
          className={clsx(styles.paginate, styles.prev)}
          disabled={disablePrev}
          onClick={() => {
            debounceClick(prevFunc);
          }}
        >
          <ArrowIcon />
        </button>
        <button
          className={clsx(styles.paginate, styles.next)}
          disabled={disableNext}
          onClick={() => {
            debounceClick(() => nextFunc());
          }}
        >
          <ArrowIcon />
        </button>
        <div onClick={handleClickOutside}>
          <Slider
            className={clsx(styles.slider)}
            onSwipe={swipeDirection => {
              if (swipeDirection === "up") {
                nextFunc();
              } else if (swipeDirection === "down") {
                prevFunc();
              }
            }}
            key={isShowSub ? "s" : "m"}
            ref={slider}
            initialSlide={isShowSub ? currentFlatSubPage - 1 : currentPage - 1}
            {...settings}
          >
            {(isShowSub ? flattenSubImages : images).map((image, i) => {
              return (
                <ImagePageView
                  imageUrl={image}
                  imageType={props.lesson.image_type}
                  page={i + 1}
                  outerSize={wrapperSize}
                  key={i}
                />
              );
            })}
          </Slider>
          <div
            className={clsx(
              styles.lessonImageMenuBar,
              showLessonImageMenu && styles.isOpen,
              isShowSub && styles.hidden,
            )}
            onClick={e => {
              e.stopPropagation();
              if (!showLessonImageMenu) setShowLessonImageMenu(true);
            }}
          >
            {showLessonImageMenu ? (
              <div className={styles.imageMenuContent}>
                {props.lesson.images.map((image, index) => (
                  <div
                    key={image.image_url}
                    className={clsx(
                      styles.imageThumbnail,
                      currentPage === index + 1 && styles.active,
                    )}
                  >
                    <div
                      className={styles.thumbnailWrapper}
                      onClick={e => {
                        e.stopPropagation();
                        setNewPage(index + 1);
                      }}
                    >
                      <img src={image.image_url} alt="" />
                      {props.lesson.issues?.some(
                        issue => issue.page === index + 1,
                      ) && (
                        <div className={styles.workIcon}>
                          <PencilIcon />
                        </div>
                      )}

                      <div className={styles.pageTag}>
                        <span>{index === 0 ? "表紙" : `P.${index}`}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <ArrowLeftIcon />
            )}
          </div>
        </div>
        {!!onePagePortfolioAssessmentId && OPPAType === "before" && (
          <BeforeModal
            isTeacher
            classList={props.classList}
            assessmentId={onePagePortfolioAssessmentId}
            assessmentData={state.one_page_portfolio_assessment}
            canViewEveryoneAnswers={
              state.one_page_portfolio_assessment_answers_visibility
            }
            onNext={() => debounceClick(() => nextFunc(true))}
            onClose={() => setOnePagePortfolioAssessmentId(undefined)}
          />
        )}
        {!!onePagePortfolioAssessmentId && OPPAType === "after" && (
          <AfterModal
            isTeacher
            classList={props.classList}
            assessmentId={onePagePortfolioAssessmentId}
            assessmentData={state.one_page_portfolio_assessment}
            referenceIssues={state.reference_issues ?? []}
            canViewEveryoneAnswers={
              state.one_page_portfolio_assessment_answers_visibility
            }
            onNext={() => debounceClick(() => nextFunc(true))}
            onClose={() => setOnePagePortfolioAssessmentId(undefined)}
          />
        )}
        {isShowCompleteLessonButton && (
          <div className={styles.completedButton}>
            <Button
              handleClick={() => setIsShowLessonCompleteConfirmModal(true)}
            >
              授業を終了する
            </Button>
          </div>
        )}
        {isShowLessonCompleteConfirmModal && (
          <LessonCompleteConfirmModal
            title={"このクラスの授業は終了しましたか？"}
            buttons={[
              {
                title: "いいえ",
                action: () => {
                  setIsShowLessonCompleteConfirmModal(false);
                },
                type: "None",
              },
              {
                title: "はい",
                action: () => {
                  setIsShowLessonCompleteConfirmModal(false);
                  setIsShowEvaluationLessonModal(true);
                },
                type: "Success",
              },
            ]}
            onClose={() => setIsShowLessonCompleteConfirmModal(false)}
          />
        )}
        {isShowEvaluationLessonModal && (
          <EvaluationLessonModal
            teacherLessonRetrospectives={state.teacher_lesson_retrospectives}
            onRatingCompleted={(score, lessonDeliveryScore, groupWorkScore) => {
              props.postCompletedLesson();
              props.postCompletedLessonRetrospectives(
                score,
                lessonDeliveryScore,
                groupWorkScore,
              );
              setIsShowEvaluationLessonModal(false);
              window.location.href = `${process.env.REACT_APP_ONE_MENU_URL}/class_retrospect/${props.classList[0]}?teaching_material_id=${props.teachingMaterialId}&lesson_id=${props.lesson.id}${extra}`;
            }}
            onClose={() => setIsShowEvaluationLessonModal(false)}
          />
        )}
      </div>
      {isShowActionModal && (
        <>
          {!actionModal && (
            <ActionBubbleIcon onClose={() => setActionModal(!actionModal)} />
          )}
          {actionModal && (
            <ActionBubbleModal onClose={() => setActionModal(false)}>
              <ProjectionScreenModal onClose={() => setActionModal(false)} />
            </ActionBubbleModal>
          )}
        </>
      )}
      <div
        className={styles.video}
        style={{
          display: showVideo && videoOptions.url.length > 0 ? "block" : "none",
        }}
      >
        <div className={styles.close} onClick={handleCloseVideo}>
          <IconClose />
        </div>
        {!!videoOptions.url && (
          <VideoJS
            key={videoOptions.url}
            className={styles.videojs}
            playing={videoOptions.playing}
            showVideo={showVideo}
            sourceUrl={videoOptions.url}
          />
        )}
      </div>
      {showTimer && (
        <div
          className={styles.timer}
          onMouseDown={onTimerDragStart}
          style={{
            transform: `translate(${timerPos.x}px, ${timerPos.y}px)`,
            cursor: "move",
          }}
        >
          <Timer
            className={styles.timer__timerInShareScreen}
            onClose={() => setShowTimer(false)}
          />
        </div>
      )}
      {isShowThreeRules && (
        <ModalThreeRules
          onClose={() => {
            setIsShowThreeRules(false);
          }}
        />
      )}
      {isShowAnswersModal && (
        <ModalAnswers
          is_display_all_schools_answer={
            !!props.lesson.is_display_all_schools_answer
          }
          issueID={issueID}
          classIDs={props.classList}
          roundNo={props.roundNo}
          isMaterial
          onClose={() => {
            setIsShowAnswersModal(false);
          }}
        />
      )}
      {showModalEditGroupClass && (
        <ModalEditGroupClass
          classIDs={props.classList}
          onClose={() => setShowModalEditGroupClass(false)}
        />
      )}
      <div
        className={clsx(
          styles.rightSideBar,
          !!props.isFromVideoLesson && styles.animate,
        )}
      >
        <div className={styles.topButtons}>
          <button
            className={styles.switchButton}
            disabled={!props.hasVideoChapter}
            onClick={props.onSwitchToVideo}
          >
            <NaviIcon />
            ナビ動画
            <ArrowIcon className={styles.iconArrow} />
          </button>
          <button
            disabled={!currentVideo}
            className={clsx(styles.actionBtn, styles.primary)}
            onClick={handleOpenVideo}
          >
            動画
          </button>
          <button
            disabled={!currentIssue}
            className={clsx(styles.actionBtn, styles.primary)}
            onClick={() => {
              setIsShowAnswersModal(true);
            }}
          >
            みんなの回答
          </button>
          <button
            className={styles.actionBtn}
            onClick={() => {
              setIsShowThreeRules(!isShowThreeRules);
            }}
          >
            3つのルール
          </button>
          <button
            className={`${clsx(styles.actionBtn)} ${styles.mb16}`}
            onClick={() => {
              setShowTimer(!showTimer);
            }}
          >
            タイマー
          </button>
          <button
            className={styles.actionBtn}
            onClick={() => {
              setShowModalEditGroupClass(!showModalEditGroupClass);
            }}
          >
            グループ設定
          </button>
          {props.lesson.sub_images && (
            <button
              className={styles.toggleSubImages}
              onClick={() => {
                if (isShowSub) {
                  setPrevSubPageState({
                    page: currentPage,
                    subPage: currentSubPage,
                  });
                  setNewPage(currentPage);
                } else {
                  let newSubPage = 1;
                  if (
                    prevSubPageState &&
                    prevSubPageState.page === currentPage
                  ) {
                    newSubPage = prevSubPageState.subPage;
                  }
                  setNewPage(currentPage, newSubPage);
                }
              }}
            >
              <div
                className={clsx(
                  styles.toggleBtn,
                  isShowSub && styles.activeBtn,
                )}
              >
                補助スライド
              </div>
              <div
                className={clsx(
                  styles.toggleBtn,
                  !isShowSub && styles.activeBtn,
                )}
              >
                教材
              </div>
            </button>
          )}
        </div>
        <div className={styles.bottomButtons}>
          {props.maxRoundNo > 1 && (
            <DropdownMenu
              className={styles.dropdown}
              classNameOptions={styles.dropdownOptions}
              options={roundOption}
              defaultValue={roundOption[props.roundNo - 1]}
              openUpwards
              onChange={value => {
                handleChangeRoundNo(value);
              }}
            />
          )}
          <div className={styles.navButtons}>
            <button
              className={styles.actionBtn}
              disabled={props.lesson.num <= 2}
              onClick={prevLesson}
            >
              前のLesson
            </button>
            <button
              className={styles.actionBtn}
              disabled={
                !props.nextLessonId && props.maxRoundNo === props.roundNo
              }
              onClick={nextLesson}
            >
              次のLesson
            </button>
          </div>
          {totalEstimatedTime > 0 && (
            <div className={styles.progressWrapper}>
              <p className={styles.progressTitle}>Lesson進捗率</p>
              <div className={styles.total}>
                <p className={styles.circle} style={getCircleStyle()} />
                <p className={styles.count}>{getPercentage()}％</p>
              </div>
              {/* ページタイトルの表示
              <ul className={styles.topTitleWrapper}>
                {props.lesson.images
                  .filter(image => image.page_title)
                  .map((image, index) => (
                    <li
                      style={{
                        color:
                          currentPage === image.page_no ? "#ffffff" : "#6E6E6E",
                        listStyleType:
                          currentPage === image.page_no ? "disc" : "circle",
                      }}
                      key={`page_title_${index}`}
                    >
                      {image.page_title && getPageTitle(image.page_title)}
                    </li>
                  ))}
              </ul>
              */}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(MaterialForImage);
